import { useCallback, useSyncExternalStore } from 'react';

/**
 * Tema claro/escuro do app.
 *
 * - Preferência por aparelho em localStorage ('dezza:theme'): 'light' | 'dark' | 'system'.
 * - Aplicada como data-theme no <html>. O CSS (theme.css) resolve 'system' com
 *   prefers-color-scheme, então não é preciso JavaScript para acompanhar o sistema.
 * - Sem piscar: chame `initTheme()` antes do primeiro render ou use `themeBootScript`
 *   (inline) / um <script src> síncrono no <head> com o mesmo conteúdo.
 */
export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'dezza:theme';
const THEMES: readonly ThemePreference[] = ['light', 'dark', 'system'];
const DARK_QUERY = '(prefers-color-scheme: dark)';

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

function parse(raw: string | null): ThemePreference | null {
  if (!raw) return null;
  // aceita o valor puro ('dark') e o formato JSON de usePersistentState ('"dark"')
  const v = raw.replace(/^"|"$/g, '') as ThemePreference;
  return THEMES.includes(v) ? v : null;
}

/** Preferência salva no aparelho (padrão: acompanhar o sistema). */
export function getStoredTheme(): ThemePreference {
  if (!isBrowser) return 'system';
  try {
    return parse(window.localStorage.getItem(THEME_STORAGE_KEY)) ?? 'system';
  } catch {
    return 'system';
  }
}

export function systemPrefersDark(): boolean {
  return isBrowser && typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;
}

export function resolveTheme(pref: ThemePreference): ResolvedTheme {
  if (pref === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return pref;
}

/** Evita que todas as transições de cor rodem ao mesmo tempo durante a troca de tema. */
function withoutTransitions(fn: () => void): void {
  const style = document.createElement('style');
  style.appendChild(document.createTextNode('*,*::before,*::after{transition:none!important}'));
  document.head.appendChild(style);
  fn();
  // força o recálculo antes de devolver as transições
  void window.getComputedStyle(document.body).opacity;
  window.setTimeout(() => style.remove(), 1);
}

/** Aplica a preferência no <html> (data-theme). Não salva nada. */
export function applyTheme(pref: ThemePreference, root: HTMLElement = document.documentElement): void {
  root.setAttribute('data-theme', pref);
}

const listeners = new Set<() => void>();
let current: ThemePreference | null = null;

function emit(): void {
  for (const l of listeners) l();
}

/** Troca e salva o tema deste aparelho. */
export function setTheme(pref: ThemePreference): void {
  if (!isBrowser) return;
  current = pref;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, pref);
  } catch {
    /* modo privado: vale só para esta sessão */
  }
  withoutTransitions(() => applyTheme(pref));
  emit();
}

/**
 * Aplica o tema salvo antes do primeiro render (main.tsx).
 * `force` fixa um tema e ignora a preferência (ex.: catálogo da loja sempre claro).
 */
export function initTheme(options: { force?: ResolvedTheme } = {}): ThemePreference {
  if (!isBrowser) return 'system';
  const pref = options.force ?? getStoredTheme();
  current = pref;
  applyTheme(pref);
  return pref;
}

/**
 * Script inline equivalente a `initTheme()`, para o <head> do index.html (roda antes da pintura).
 * Onde a CSP bloqueia script inline, sirva o mesmo conteúdo como arquivo (ex.: /theme-boot.js).
 */
export const themeBootScript = `(function(){try{var k='${THEME_STORAGE_KEY}',v=(localStorage.getItem(k)||'').replace(/^"|"$/g,'');if(v!=='light'&&v!=='dark')v='system';document.documentElement.setAttribute('data-theme',v)}catch(e){document.documentElement.setAttribute('data-theme','system')}})();`;

// ------------------------------------------------------------------ store para o React
function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  if (!isBrowser) return () => listeners.delete(cb);
  const onStorage = (e: StorageEvent) => {
    if (e.key !== THEME_STORAGE_KEY) return;
    current = parse(e.newValue) ?? 'system';
    applyTheme(current);
    emit();
  };
  const mql = typeof window.matchMedia === 'function' ? window.matchMedia(DARK_QUERY) : null;
  window.addEventListener('storage', onStorage);
  mql?.addEventListener('change', cb);
  return () => {
    listeners.delete(cb);
    window.removeEventListener('storage', onStorage);
    mql?.removeEventListener('change', cb);
  };
}

const getPreference = (): ThemePreference => current ?? (current = getStoredTheme());
const getResolved = (): ResolvedTheme => resolveTheme(getPreference());

export interface UseThemeResult {
  /** Preferência escolhida ('system' acompanha o aparelho). */
  theme: ThemePreference;
  /** Tema efetivamente em uso agora. */
  resolvedTheme: ResolvedTheme;
  setTheme: (pref: ThemePreference) => void;
  /** Alterna entre claro e escuro (sai do modo "sistema"). */
  toggleTheme: () => void;
}

/** Tema atual e funções para trocá-lo (sincroniza entre abas e com o sistema). */
export function useTheme(): UseThemeResult {
  const theme = useSyncExternalStore(subscribe, getPreference, () => 'system' as ThemePreference);
  const resolvedTheme = useSyncExternalStore(subscribe, getResolved, () => 'light' as ResolvedTheme);
  const toggleTheme = useCallback(() => setTheme(resolveTheme(getPreference()) === 'dark' ? 'light' : 'dark'), []);
  return { theme, resolvedTheme, setTheme, toggleTheme };
}
